import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import {
  $createTextNode,
  $getSelection,
  $insertNodes,
  $isRangeSelection,
  $isTextNode,
  COMMAND_PRIORITY_LOW,
  KEY_DOWN_COMMAND,
} from "lexical";
import { useEffect } from "react";
import { $createStockNode } from "../nodes/StockNode";

/**
 * 输入变换插件
 *
 * 功能：
 * - 监听 KEY_DOWN_COMMAND
 * - 按空格/标点时自动将 $SYMBOL 转换为 StockNode
 * - 按空格/标点时自动将 #tag 转换为 TagNode
 */

export function InputTransformPlugin(): null {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    return editor.registerCommand(
      KEY_DOWN_COMMAND,
      (event) => {
        // 触发转换的字符：空格、逗号、分号、回车等
        // 排除 . 和 -，因为它们可能出现在股票代码中（如 BRK.A）
        const triggerChars = /^[\s,;:!?()[\]{}"']$/;
        const isEnterKey = event.key === "Enter";

        if (!triggerChars.test(event.key) && !isEnterKey) {
          return false;
        }

        editor.update(() => {
          const selection = $getSelection();
          if (!$isRangeSelection(selection) || !selection.isCollapsed()) {
            return;
          }

          const anchorNode = selection.anchor.getNode();
          if (!$isTextNode(anchorNode)) {
            return;
          }

          const content = anchorNode.getTextContent();
          const offset = selection.anchor.offset;
          const beforeCursor = content.slice(0, offset);

          // Stock: $NVDA, $nvda, $BRK.A, $brk.b
          // 支持 1-5 个字母（大小写均可），可选的点号后跟 1-2 个字母
          const stockMatch = beforeCursor.match(/\$([a-zA-Z]{1,5}(?:\.[a-zA-Z]{1,2})?)$/);
          if (stockMatch) {
            const symbol = stockMatch[1].toUpperCase(); // 统一转为大写
            const matchLength = stockMatch[0].length; // $NVDA (不包含触发字符)

            // 选中文本（$SYMBOL）
            selection.anchor.offset -= matchLength;
            selection.focus.offset = selection.anchor.offset + matchLength;

            // 删除选中的文本
            selection.removeText();

            // 插入 StockNode
            const stockNode = $createStockNode(symbol, "neutral", null);
            $insertNodes([stockNode]);

            // 只对 Enter 键手动插入换行
            if (isEnterKey) {
              const newlineNode = $createTextNode("\n");
              stockNode.insertAfter(newlineNode);
              newlineNode.select(0, 0);
            } else {
              // 非Enter键：确保光标在插入的节点之后，让 Lexical 继续处理
              // 获取插入后的节点位置
              const insertedNode = stockNode.getLatest();
              if (insertedNode) {
                const nextSibling = insertedNode.getNextSibling();
                if (nextSibling && $isTextNode(nextSibling)) {
                  // 如果下一个是文本节点，选中它
                  nextSibling.select(0, 0);
                } else {
                  // 否则，在 StockNode 后创建一个空文本节点供 Lexical 插入
                  const textNode = $createTextNode("");
                  insertedNode.insertAfter(textNode);
                  textNode.select(0, 0);
                }
              }
            }
            return;
          }

          // Tag: #AI, #测试, #标签1 (支持中文、字母、数字、连字符、下划线)
          // Note: Tag auto-conversion is DISABLED per SSOT requirement.
          // Users must select tags via LexicalSuggestionsPlugin which ensures tagId is populated.
          // Note: Previous dead code (tagMatch if block) has been removed.
        });

        // 返回 false，让 Lexical 继续处理字符插入
        // 但我们已经删除了 $SYMBOL 并插入了节点，所以不会重复
        return false;
      },
      COMMAND_PRIORITY_LOW,
    );
  }, [editor]);

  return null;
}
